import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import PlatformMetrics from './components/PlatformMetrics';
import UserManagementCenter from './components/UserManagementCenter';
import Button from '../../components/ui/Button';

const AdminDashboard = () => {
  const { user, userProfile, isAuthenticated, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [authVerified, setAuthVerified] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
    // Redirect non-admin users to home page
    if (!loading && isAuthenticated && !isAdmin) {
      navigate('/home-page');
    }
    // Set auth verified after role confirmation
    if (!loading && isAuthenticated && isAdmin) {
      setAuthVerified(true);
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="font-body text-muted-foreground">Verifying admin credentials...</span>
        </div>
      </div>
    );
  }

  // Access denied for non-admin users
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="ShieldAlert" size={40} className="text-error" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-foreground mb-3">Access Denied</h2>
          <p className="font-body text-muted-foreground mb-2">Administrator privileges required</p>
          <p className="font-body text-sm text-muted-foreground mb-8">You do not have permission to access the admin dashboard. This area is restricted to verified platform administrators only.</p>
          <Button onClick={() => navigate('/home-page')} className="w-full sm:w-auto">
            <Icon name="Home" size={18} className="mr-2" />
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header with Authentication Confirmation */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="Shield" size={28} strokeWidth={2.5} className="text-white" />
                </div>
                <div>
                  <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-1">Admin Dashboard</h1>
                  <p className="font-body text-muted-foreground">Comprehensive platform oversight and management</p>
                </div>
              </div>
              
              {/* Role Verification Badge */}
              {authVerified && (
                <div className="flex items-center space-x-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-primary" />
                  <div className="text-left">
                    <p className="font-body text-xs text-muted-foreground">Authenticated as</p>
                    <p className="font-body font-semibold text-sm text-foreground">Administrator</p>
                  </div>
                </div>
              )}
            </div>

            {/* Admin Info Card */}
            <div className="bg-card rounded-lg shadow-elevation-2 p-4 border-l-4 border-primary">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="font-body text-sm text-foreground mb-1">
                    <span className="font-semibold">Welcome, {userProfile?.full_name || user?.email}</span>
                  </p>
                  <p className="font-body text-xs text-muted-foreground">
                    You have full administrative access to platform management tools, user oversight, and system analytics.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Metrics Overview */}
          <div className="mb-8">
            <PlatformMetrics />
          </div>

          {/* User Management Center */}
          <div className="mb-8">
            <UserManagementCenter />
          </div>

          {/* Quick Actions */}
          <div className="bg-card rounded-lg shadow-elevation-2 p-6">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4 flex items-center">
              <Icon name="Zap" size={22} className="text-primary mr-2" />
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => navigate('/admin-platform-management')}
                className="flex items-center space-x-3 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-smooth text-left group"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-smooth">
                  <Icon name="Settings" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="font-body font-semibold text-sm text-foreground">Platform Management</p>
                  <p className="font-body text-xs text-muted-foreground">Full admin controls</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/dispute-resolution')}
                className="flex items-center space-x-3 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-smooth text-left group"
              >
                <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center group-hover:bg-orange-500/20 transition-smooth">
                  <Icon name="AlertTriangle" size={20} className="text-orange-500" />
                </div>
                <div>
                  <p className="font-body font-semibold text-sm text-foreground">Dispute Resolution</p>
                  <p className="font-body text-xs text-muted-foreground">Review conflicts</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/reviews-management')}
                className="flex items-center space-x-3 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-smooth text-left group"
              >
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20 transition-smooth">
                  <Icon name="Star" size={20} className="text-blue-500" />
                </div>
                <div>
                  <p className="font-body font-semibold text-sm text-foreground">Reviews Management</p>
                  <p className="font-body text-xs text-muted-foreground">Monitor feedback</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/payout-management')}
                className="flex items-center space-x-3 p-4 bg-muted hover:bg-muted/80 rounded-lg transition-smooth text-left group"
              >
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center group-hover:bg-green-500/20 transition-smooth">
                  <Icon name="DollarSign" size={20} className="text-green-500" />
                </div>
                <div>
                  <p className="font-body font-semibold text-sm text-foreground">Financial Oversight</p>
                  <p className="font-body text-xs text-muted-foreground">Transaction monitoring</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;